import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, ErrorState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import AttendanceWidget from "@/components/AttendanceWidget";
import MobileDashboard from "@/components/MobileDashboard";
import { splitListItems } from "@/lib/utils";
import {
  ClipboardList, ShieldCheck, CalendarDays, GraduationCap, FileText,
  TrendingUp, BookOpen, Info, ArrowUpRight, Users, Clock, MapPin
} from "lucide-react";

// "09:00" -> "9:00 AM"
function formatShiftTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function LocationShiftBanner() {
  // Shares the "attendanceStatus" cache key with AttendanceWidget, so this
  // renders instantly from the same fetch rather than triggering a second
  // network call — and stays in sync the moment HR changes either one,
  // since both re-fetch together whenever that query is invalidated.
  const { data: status, isLoading } = useQuery({
    queryKey: ["attendanceStatus"], queryFn: async () => (await api.get("/my/attendance/status")).data,
  });
  if (isLoading || !status) return null;
  if (status.field_staff) {
    return (
      <Card className="p-4 border-slate-200 shadow-none mb-6 flex items-center gap-3" data-testid="dash-location-shift">
        <MapPin className="h-5 w-5 text-teal shrink-0" />
        <p className="text-sm text-navy">You're set up as <span className="font-semibold">Field / Remote Staff</span> — no fixed location or shift timing applies to you.</p>
      </Card>
    );
  }
  const locations = status.assigned_locations || [];
  const shift = status.shift;
  if (locations.length === 0 && !shift) return null;
  return (
    <Card className="p-4 border-slate-200 shadow-none mb-6" data-testid="dash-location-shift">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-teal shrink-0" />
          <div>
            <p className="overline text-slate-400">Location</p>
            {locations.length === 0 ? (
              <p className="text-sm font-semibold text-amber-600">Not yet assigned — contact HR</p>
            ) : (
              <p className="text-sm font-semibold text-navy">
                {locations.map((l) => l.name).join(", ")}
                {locations.length === 1 && locations[0].address && <span className="text-xs text-slate-400 font-normal"> · {locations[0].address}</span>}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-teal shrink-0" />
          <div>
            <p className="overline text-slate-400">Shift Timing</p>
            {shift ? (
              <p className="text-sm font-semibold text-navy">{formatShiftTime(shift.shift_start)} · {shift.shift_hours} hrs{shift.saturday_half_day && <span className="text-xs text-slate-400 font-normal"> · Saturdays 4 hrs</span>}</p>
            ) : (
              <p className="text-sm font-semibold text-amber-600">Not yet assigned — contact HR</p>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 ml-auto">Set by HR — this updates automatically if either changes.</p>
      </div>
      {status.cover_today && (
        <p className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="dash-cover-today">
          Today you are the backup for <span className="font-semibold">{status.cover_today.for_name}</span> — your duty starts at <span className="font-semibold">{formatShiftTime(status.cover_today.shift_start)}</span>.
        </p>
      )}
    </Card>
  );
}

// Some HR-entered fields (additional charge, job responsibility, authorized
// activity) are plain single-value text inputs, so a multi-item list pasted
// in one go lands as one long blob instead of several records. splitListItems
// detects that and lets us render it as a proper bullet list instead of one
// run-on line, without touching the underlying stored data.
function InfoTile({ label, value }) {
  const items = splitListItems(value);
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
      <p className="overline text-slate-400">{label}</p>
      {items.length > 1 ? (
        <ul className="mt-1 space-y-0.5">
          {items.map((item, i) => (
            <li key={i} className="text-sm font-semibold text-navy flex items-start gap-1.5">
              <span className="h-1 w-1 rounded-full bg-navy/40 mt-2 shrink-0" />{item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm font-semibold text-navy mt-0.5">{value || "—"}</p>
      )}
    </div>
  );
}

export default function EmployeeDashboard() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["myDash"],
    queryFn: async () => (await api.get("/my/dashboard")).data,
  });
  if (isError) {
    const timedOut = error?.code === "ECONNABORTED";
    return (
      <ErrorState
        message={timedOut ? "This is taking longer than usual to load." : "Couldn't load your HR profile."}
        onRetry={refetch}
      />
    );
  }
  if (isLoading || !data) return <Loading label="Loading your HR profile…" />;
  const c = data.current;

  return (
    <>
    <div className="lg:hidden"><MobileDashboard data={data} /></div>
    <div className="hidden lg:block">
      <PageHeader overline={`Employee · ${data.identity.employee_id}`} title={`Welcome, ${data.identity.name.split(" ")[0]}`}
        subtitle="Your current HR status at a glance. Fields are maintained by HR." />

      <LocationShiftBanner />

      <Card className="p-6 border-slate-200 shadow-none mb-6" data-testid="dash-attendance">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-teal" />
          <h3 className="font-serif-display text-xl text-navy">Today's Attendance</h3>
          <Link to="/attendance" className="ml-auto text-xs text-teal flex items-center gap-1">Full history <ArrowUpRight className="h-3 w-3" /></Link>
        </div>
        <AttendanceWidget />
      </Card>

      {data.is_reporting_officer && (
        <Link to="/team">
          <Card className="p-4 border-teal/30 bg-teal/5 shadow-none mb-6 flex items-center gap-3 btn-lift" data-testid="dash-team-banner">
            <Users className="h-5 w-5 text-teal shrink-0" />
            <p className="text-sm text-navy">You're a Reporting Officer. Visit <span className="font-semibold">My Team</span> to review your team's pending leave and grievance requests.</p>
            <ArrowUpRight className="h-4 w-4 text-teal ml-auto shrink-0" />
          </Card>
        </Link>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6 stagger">
        <InfoTile label="Designation" value={c.designation} />
        <InfoTile label="Position" value={c.position} />
        <InfoTile label="Department" value={c.department} />
        <InfoTile label="Grade" value={c.grade} />
        <InfoTile label="Employment Type" value={c.employment_type} />
        <InfoTile label="Reporting Manager" value={c.reporting_manager} />
        <InfoTile label="Date of Joining" value={c.doj} />
        <InfoTile label="Project / Site" value={c.project_site} />
        {c.additional_charge && <InfoTile label="Additional Charge" value={c.additional_charge} />}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4"><ClipboardList className="h-5 w-5 text-teal" />
            <h3 className="font-serif-display text-xl text-navy">Current Job Responsibilities</h3>
            <Link to="/responsibilities" className="ml-auto text-xs text-teal flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" /></Link>
          </div>
          {data.responsibilities.length === 0 ? <p className="text-sm text-slate-400 py-6">No responsibilities assigned yet.</p> : (
            <ul className="space-y-2" data-testid="dash-responsibilities">
              {data.responsibilities.flatMap((r) =>
                splitListItems(r.text).map((item, i) => (
                  <li key={`${r.id}-${i}`} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal mt-1.5 shrink-0" />{item}
                  </li>
                ))
              )}
            </ul>
          )}
        </Card>

        <Card className="p-6 border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-teal" />
            <h3 className="font-serif-display text-xl text-navy">My Authorizations</h3>
            <Link to="/authorizations" className="ml-auto text-xs text-teal flex items-center gap-1">View <ArrowUpRight className="h-3 w-3" /></Link>
          </div>
          {data.authorizations.length === 0 ? <p className="text-sm text-slate-400 py-6">No active authorizations.</p> : (
            <div className="space-y-2" data-testid="dash-authorizations">
              {data.authorizations.map((a) => {
                const items = splitListItems(a.activity);
                return (
                  <div key={a.id} className="rounded-md border border-slate-100 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-navy truncate">{items.length > 1 ? `${items.length} authorized items` : items[0]}</span>
                      <StatusBadge status={a.status === "Authorized" ? "Active" : a.status} />
                    </div>
                    {items.length > 1 && (
                      <ul className="mt-1.5 space-y-1">
                        {items.map((item, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                            <span className="h-1 w-1 rounded-full bg-teal/60 mt-1.5 shrink-0" />{item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Link to="/leave"><Card className="p-5 border-slate-200 shadow-none btn-lift h-full" data-testid="dash-leave-tile">
          <CalendarDays className="h-6 w-6 text-teal mb-2" />
          <p className="font-serif-display text-lg text-navy">My Leave</p>
          {(data.leave.balances || []).length === 0 ? (
            <p className="text-xs text-slate-500 mt-1">Balances not yet configured.</p>
          ) : (
            <div className="mt-1 space-y-0.5">
              {data.leave.balances.map((b) => (
                <p key={b.code} className="text-xs text-slate-600 flex justify-between"><span>{b.code}</span><span className="font-semibold text-navy">{b.available ?? "—"}</span></p>
              ))}
              {data.leave.pending > 0 && <p className="text-[11px] text-amber-600 mt-1">{data.leave.pending} request(s) pending</p>}
            </div>
          )}
        </Card></Link>
        <Card className="p-5 border-slate-200 shadow-none">
          <GraduationCap className="h-6 w-6 text-teal mb-2" />
          <p className="font-serif-display text-3xl text-navy">{data.counts.qualifications}</p>
          <p className="text-sm text-slate-500">Verified Qualifications</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <FileText className="h-6 w-6 text-teal mb-2" />
          <p className="font-serif-display text-3xl text-navy">{data.counts.documents}</p>
          <p className="text-sm text-slate-500">HR Documents</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <TrendingUp className="h-6 w-6 text-teal mb-2" />
          <p className="font-serif-display text-lg text-navy">Appraisal</p>
          <p className="text-xs text-slate-500 mt-1">{data.performance.status}</p>
        </Card>
      </div>

      <Card className="p-5 border-slate-200 shadow-none mt-4 bg-navy/5">
        <div className="flex items-start gap-3">
          <BookOpen className="h-5 w-5 text-navy mt-0.5" />
          <div>
            <p className="font-semibold text-navy text-sm">IntenCiv Service Manual</p>
            <p className="text-sm text-slate-600 mt-1">For travel, compensation, benefits and all company-wide policies, please refer to the current approved IntenCiv Human Resource Management and Service Manual for the authoritative policy. (Service Manual & Policies section coming soon.)</p>
          </div>
        </div>
      </Card>
    </div>
    </>
  );
}
